import {
  ActionContext as VuexActionContext,
  Store
} from 'vuex'

import AddressSearchService from '@/services/address-search.service'

import { Address, Apartment } from '@/types/area-response.types'

enum Property {
  Loading = 'loading',
  Addresses = 'addresses',
  SelectedAddress = 'selectedAddress',
  Apartments = 'apartments'
}

enum Setter {
  Loading = 'setLoading',
  Addresses = 'setAddresses',
  SelectedAddress = 'setSelectedAddress',
  Apartments = 'setApartments'
}

enum Getter {
  Loading = 'isLoading',
  Addresses = 'getAddresses',
  SelectedAddress = 'getSelectedAddress',
  Apartments = 'getApartments'
}

enum Action {
  FetchAddresses = 'fetchAddresses',
  ClearAddresses = 'clearAddresses',
  FetchApartments = 'fetchApartments',
  UpdateSelectedAddress = 'updateSelectedAddress',
  SearchStringChanged = 'searchStringChanged',
  SearchCanceled = 'searchCanceled'
}

export interface State {
  [Property.Loading]: boolean;
  [Property.Addresses]: Address[];
  [Property.SelectedAddress]: Address | null;
  [Property.Apartments]: Apartment[];
}

export type ActionContext = VuexActionContext<State, {}>

export const Getters = {
  [Getter.Loading]: (store: Store<ActionContext>): boolean => store.getters[`AddressSearch/${Getter.Loading}`],
  [Getter.Addresses]: (store: Store<ActionContext>): Address[] => store.getters[`AddressSearch/${Getter.Addresses}`],
  [Getter.SelectedAddress]: (store: Store<ActionContext>): Address | null => store.getters[`AddressSearch/${Getter.SelectedAddress}`],
  [Getter.Apartments]: (store: Store<ActionContext>): Apartment[] => store.getters[`AddressSearch/${Getter.Apartments}`]
}

export const Actions = {
  [Action.ClearAddresses]: (store: Store<ActionContext>): Promise<void> => store.dispatch(`AddressSearch/${Action.ClearAddresses}`),
  [Action.FetchAddresses]: (store: Store<ActionContext>, fragment: string): Promise<void> => store.dispatch(`AddressSearch/${Action.FetchAddresses}`, fragment),
  [Action.FetchApartments]: (store: Store<ActionContext>): Promise<void> => store.dispatch(`AddressSearch/${Action.FetchApartments}`),
  [Action.UpdateSelectedAddress]: (store: Store<ActionContext>, address: Address | null): Promise<void> => store.dispatch(`AddressSearch/${Action.UpdateSelectedAddress}`, address),
  [Action.SearchStringChanged]: (store: Store<ActionContext>): Promise<void> => store.dispatch(`AddressSearch/${Action.SearchStringChanged}`),
  [Action.SearchCanceled]: (store: Store<ActionContext>): Promise<void> => store.dispatch(`AddressSearch/${Action.SearchCanceled}`)
}

export default {
  namespaced: true,
  state: {
    [Property.Loading]: false,
    [Property.Addresses]: [],
    [Property.SelectedAddress]: null,
    [Property.Apartments]: []
  },
  getters: {
    [Getter.Loading]: (state: State) => state[Property.Loading],
    [Getter.Addresses]: (state: State) => state[Property.Addresses],
    [Getter.SelectedAddress]: (state: State) => state[Property.SelectedAddress],
    [Getter.Apartments]: (state: State) => state[Property.Apartments]
  },
  mutations: {
    [Setter.Loading]: (state: State, loading: boolean) => state[Property.Loading] = loading,
    [Setter.Addresses]: (state: State, addresses: Address[]) => state[Property.Addresses] = addresses,
    [Setter.SelectedAddress]: (state: State, selectedAddress: Address | null) => state[Property.SelectedAddress] = selectedAddress,
    [Setter.Apartments]: (state: State, apartments: Apartment[]) => state[Property.Apartments] = apartments
  },
  actions: {
    [Action.FetchAddresses]: async ({ commit }: ActionContext, fragment: string) => {
      try {
        const { Addresses } = await AddressSearchService.fetchAddresses(fragment)

        commit(Setter.Addresses, Addresses)
      } catch {
        // nothing
      } finally {
        commit(Setter.Loading, false)
      }
    },
    [Action.FetchApartments]: async ({ commit, state }: ActionContext) => {
      const selectedAddress = state[Property.SelectedAddress]

      if (!selectedAddress) { return }

      commit(Setter.Loading, true)

      try {
        const { FullAddress, City } = selectedAddress

        const { ApartmentNumbers } = await AddressSearchService.fetchApartments(FullAddress, City)

        commit(Setter.Apartments, ApartmentNumbers)
      } catch {
        // nothing
      } finally {
        commit(Setter.Loading, false)
      }
    },
    [Action.ClearAddresses]: ({ commit }: ActionContext): void => {
      commit(Setter.SelectedAddress, null)
      commit(Setter.Addresses, [])
      commit(Setter.Apartments, [])
    },
    [Action.UpdateSelectedAddress]: ({ commit }: ActionContext, address: Address | null) => {
      commit(Setter.SelectedAddress, address)
    },
    [Action.SearchStringChanged]: ({ commit }: ActionContext) => {
      commit(Setter.Loading, true)
    },
    [Action.SearchCanceled]: ({ commit }: ActionContext) => {
      commit(Setter.Loading, false)
    }
  }
}
